import { useEffect, useMemo, useRef, useState } from 'react';

function fmt(sec: number): string {
  if (!Number.isFinite(sec)) return '00:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const BAR_COUNT = 44;

/** Compact custom audio player matching the concept art: gradient play button, a static bar
 *  visualization with a "played" portion, and current/total time. Wraps a native <audio>. */
export function NoteAudioPlayer({ src }: { src: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  // Deterministic bar heights per src so it looks like a waveform without decoding audio.
  const heights = useMemo(() => {
    let seed = 0;
    for (let i = 0; i < src.length; i++) seed = (seed * 31 + src.charCodeAt(i)) % 1000;
    const rand = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };
    return Array.from({ length: BAR_COUNT }, (_, i) => 0.25 + 0.75 * Math.abs(Math.sin(i * 0.6) * rand()));
  }, [src]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => setDur(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const seek = (i: number) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    a.currentTime = (i / BAR_COUNT) * dur;
  };

  const playedTo = dur ? (cur / dur) * BAR_COUNT : 0;

  return (
    <div className="flex items-center gap-3 rounded-xl border border-ui-border bg-ui-subtle p-3">
      <button
        type="button"
        onClick={toggle}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full btn-sheen text-on-primary"
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? (
          <span className="flex gap-0.5">
            <span className="h-3.5 w-1 rounded-sm bg-white" />
            <span className="h-3.5 w-1 rounded-sm bg-white" />
          </span>
        ) : (
          <span className="ml-0.5 h-0 w-0 border-y-[7px] border-l-[11px] border-y-transparent border-l-white" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex h-8 items-center gap-[2px]">
          {heights.map((h, i) => (
            <button
              key={i}
              type="button"
              onClick={() => seek(i)}
              className="h-full flex-1"
              tabIndex={-1}
              aria-hidden
            >
              <span
                className={`block w-full rounded-full ${i <= playedTo ? 'bg-accent' : 'bg-ui-border'}`}
                style={{ height: `${Math.round(h * 100)}%` }}
              />
            </button>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-xs text-ui-text3">
          <span>{fmt(cur)}</span>
          <span>{fmt(dur)}</span>
        </div>
      </div>

      <audio ref={audioRef} src={src} preload="metadata" className="hidden">
        <track kind="captions" />
      </audio>
    </div>
  );
}

export default NoteAudioPlayer;
